import { useEffect, useCallback } from 'react';
import pusher from '@/services/pusherService';
import useAuth from '@/hooks/stores/useAuth';
import useGlobalStore from '@/hooks/stores/useGlobalStore';

/**
 * Private: private-notifications.{userId} (= Echo notifications.{userId})
 * Events: .notification.sent, .notification.updated, .notifications.all-read
 */
const NOTIFICATION_ROLES = ['admin', 'recruiter', 'candidate'];

function bindBoth(channel, name, handler) {
    channel.bind(name, handler);
    if (!name.startsWith('.')) {
        channel.bind(`.${name}`, handler);
    }
}

const useNotifications = (onNotificationReceived) => {
    const { userID, isAuthenticated, userRole } = useAuth();
    const signalUnreadFromRealtime = useGlobalStore((s) => s.signalUnreadFromRealtime);

    const handleNewNotification = useCallback(
        (data) => {
            if (import.meta.env.DEV) {
                console.log('Pusher notification.sent:', data);
            }
            signalUnreadFromRealtime();
            if (onNotificationReceived) {
                onNotificationReceived({ event: 'notification.sent', ...data });
            }
        },
        [onNotificationReceived, signalUnreadFromRealtime],
    );

    const handleUpdated = useCallback(
        (data) => {
            if (import.meta.env.DEV) {
                console.log('Pusher notification.updated:', data);
            }
            void useGlobalStore.getState().refreshNotificationBellFromServer();
            if (onNotificationReceived) {
                onNotificationReceived({ event: 'notification.updated', ...data });
            }
        },
        [onNotificationReceived],
    );

    const handleAllRead = useCallback(
        (data) => {
            if (import.meta.env.DEV) {
                console.log('Pusher notifications.all-read:', data);
            }
            useGlobalStore.getState().setHasUnreadNotifications(false);
            if (onNotificationReceived) {
                onNotificationReceived({ event: 'notifications.all-read', ...data });
            }
        },
        [onNotificationReceived],
    );

    useEffect(() => {
        if (!isAuthenticated || !userID) return;
        if (!NOTIFICATION_ROLES.includes(userRole?.toLowerCase())) return;

        const channelName = `private-notifications.${userID}`;
        const channel = pusher.subscribe(channelName);

        bindBoth(channel, 'notification.sent', handleNewNotification);
        bindBoth(channel, 'notification.updated', handleUpdated);
        bindBoth(channel, 'notifications.all-read', handleAllRead);

        channel.bind('NotificationSent', handleNewNotification);
        channel.bind('.NotificationSent', handleNewNotification);
        channel.bind(
            '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
            handleNewNotification,
        );
        channel.bind(
            'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
            handleNewNotification,
        );

        if (import.meta.env.DEV) {
            console.log(`Subscribed to Pusher channel: ${channelName}`);
        }

        return () => {
            pusher.unsubscribe(channelName);
        };
    }, [
        userID,
        isAuthenticated,
        userRole,
        handleNewNotification,
        handleUpdated,
        handleAllRead,
    ]);

    return null;
};

export default useNotifications;
